import React, { createContext, useContext, useState, useEffect } from 'react';
import { CONFIG } from '../config/environment';

export interface Subscription {
  status: string;
  planTier: string;
  linkCount: number;
  linkLimit: number;
  currentPeriodEnd: string | null;
  gracePeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface User {
  id: string;
  orgId: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

export interface OnboardingStatus {
  onboarding_completed: boolean;
  questionnaire_completed: boolean;
  how_heard_about_us?: string | null;
  platform_use_case?: string | null;
  additional_details?: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  subscription: Subscription | null;
  onboardingStatus: OnboardingStatus | null;
  login: (email: string, password: string) => Promise<void>;
  loginAsDemo: () => Promise<void>;
  register: (orgId: string, email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => void;
  refreshSubscription: () => Promise<void>;
  refreshOnboardingStatus: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscription = async (authToken: string) => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/stripe/subscription`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.subscription) {
          setSubscription(data.subscription);
        }
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      // Don't throw - subscription is optional
    }
  };

  const fetchOnboardingStatus = async (authToken: string) => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/onboarding/status`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.onboarding) {
          setOnboardingStatus(data.onboarding);
        }
      }
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      // Don't throw - onboarding is optional
    }
  };

  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser({
          id: data.user.orgId, // Use orgId as id for backward compatibility
          orgId: data.user.orgId,
          email: data.user.email,
          firstName: data.user.firstName,
          lastName: data.user.lastName
        });
        setToken(tokenToVerify);

        // Fetch subscription data
        await fetchSubscription(tokenToVerify);

        // Fetch onboarding status
        await fetchOnboardingStatus(tokenToVerify);
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setSubscription(null);
        setOnboardingStatus(null);
      }
    } catch (err) {
      console.error('Token verification error:', err);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setSubscription(null);
      setOnboardingStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSubscription = async () => {
    if (token) {
      await fetchSubscription(token);
    }
  };

  const refreshOnboardingStatus = async () => {
    if (token) {
      await fetchOnboardingStatus(token);
    }
  };

  const login = async (email: string, password: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store token
      localStorage.setItem('token', data.token);
      setToken(data.token);

      // Set user
      setUser({
        id: data.user.orgId, // Use orgId as id for backward compatibility
        orgId: data.user.orgId,
        email: data.user.email,
        firstName: data.user.firstName,
        lastName: data.user.lastName
      });

      // Fetch subscription data
      await fetchSubscription(data.token);

      // Fetch onboarding status
      await fetchOnboardingStatus(data.token);

    } catch (err: any) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Store token
      localStorage.setItem('token', data.token);
      setToken(data.token);

      // Set user
      setUser({
        id: data.user.orgId, // Use orgId as id for backward compatibility
        orgId: data.user.orgId,
        email: data.user.email,
        firstName: data.user.firstName,
        lastName: data.user.lastName
      });

      // Fetch subscription data
      await fetchSubscription(data.token);

      // Fetch onboarding status
      await fetchOnboardingStatus(data.token);

    } catch (err: any) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSubscription(null);
    setOnboardingStatus(null);
    setError(null);
  };

  const loginAsDemo = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/api/auth/demo`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Demo login failed');
      localStorage.setItem('token', data.token);
      setToken(data.token);
      setUser({
        id: data.user.orgId,
        orgId: data.user.orgId,
        email: data.user.email,
        firstName: data.user.firstName,
        lastName: data.user.lastName
      });
      await fetchSubscription(data.token);
      await fetchOnboardingStatus(data.token);
    } catch (err: any) {
      setError(err.message || 'Demo login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    token,
    subscription,
    onboardingStatus,
    login,
    loginAsDemo,
    register,
    logout,
    refreshSubscription,
    refreshOnboardingStatus,
    isLoading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
